import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { 
  House, 
  Folder, 
  Plus,
  SignOut,
  Lightning,
  List,
  FileText,
  UserCircle,
  Question,
  UsersThree,
  Receipt
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/217o5tly_Marema_Logo.jpg";

const NAV_ITEMS = [
  { path: '/portal', icon: House, label: 'Dashboard', exact: true },
  { path: '/portal/quotations', icon: FileText, label: 'Quotations' },
  { path: '/portal/statement', icon: Receipt, label: 'My Statement' },
  { path: '/portal/service-request', icon: Plus, label: 'New Request' },
  { path: '/portal/sub-accounts', icon: UsersThree, label: 'My Clients' },
  { path: '/portal/profile', icon: UserCircle, label: 'Profile' },
  { path: '/portal/help', icon: Question, label: 'Help' },
];

const PortalLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/portal" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Marema Consulting" className="h-14 w-auto" />
              </Link>

              <nav className="hidden md:flex items-center gap-1">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive(item.path, item.exact)
                        ? 'text-[#4CAF50] bg-[#4CAF50]/5'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-3">
                <div className="w-8 h-8 bg-[#4CAF50] rounded-full flex items-center justify-center text-white font-medium text-sm">
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </div>
                <span className="text-sm text-gray-600">{user?.name}</span>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-800"
                data-testid="logout-btn"
              >
                <SignOut className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <List className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-[#E5E7EB] py-4">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium ${
                    isActive(item.path, item.exact)
                      ? 'text-[#0055FF] bg-[#0055FF]/5'
                      : 'text-[#4B5563]'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {title && (
          <h1 className="text-2xl font-bold text-[#0A192F] mb-6">{title}</h1>
        )}
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] py-6 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Marema Consulting. Need help?{' '}
            <a href="mailto:info@maremaconsulting.co.za" className="text-[#4CAF50] hover:underline">
              Contact Support
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PortalLayout;
